import { Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import AssessmentIcon from '@material-ui/icons/Assessment';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PlayCircleIcon from '@material-ui/icons/PlayCircleFilled';
import TableChartIcon from '@material-ui/icons/TableChart';
import "@triply/yasgui/build/yasgui.min.css";
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Errored from '../../core/Errored';
import Help from '../../core/Help';
import CopyToCipboard from '../../core/icons/CopyToClipboard';
import Loading from '../../core/Loading';
import Notification from '../../core/Notification';
import ProjectContainer from '../../core/project/ProjectContainer';
import { UserConsumer } from '../../core/providers/UserProvider';
import ReportResultsViewer from '../../core/report/ReportResultsViewer';
import SecondaryButton from '../../core/SecondaryButton';
import { ENTITLEMENTS } from '../../core/utils/constants';
import GS from '../../grafsync/grafsync';
import OntologyProjectInfoContainer from '../../ontology/ontologyproject/OntologyProjectInfoContainer';
import VocabularyProjectInfoContainer from '../../vocabulary/vocabularyproject/VocbabularyProjectInfoContainer';
import PrimaryButton from '../PrimaryButton';
import SelectProjectDialog from '../project/SelectProjectDialog';
import workerCode from '../report/workers/report-export.js';
import { globalStyles } from '../style/gstheme';
import "../style/sparql-editor.css";
import { getGraphFromProject, makeCancelable } from '../utils/utils';
import WebWorker from '../utils/workerSetup';
import QueryManager from './QueryManager';
import SaveQueryDialog from './SaveQueryDialog';
import SparqlProjectCard from './SparqlProjectCard';
import SRV from './SparqlResultsViewer';
import YasqeManager from './YasqeManager';

const styles = () => {
  return Object.assign({
    queryManagerContainer: {
      float: 'right',
      clear: 'both',
      width: '300px',
      backgroundColor: '#e5e5e5',
      paddingTop: 12,
      borderRadius: 4
    },
    yasguiContainer: {
      clear: 'both'
    }
  }, globalStyles);
};

const MAX_QUERY_LENGTH = 20000;

class SparqlManager extends Component {

  constructor(props) {
    super(props);
    this.state = {
      data: null,
      allowSavedQueries: GS.getActiveSubscriptionType() !== "Individual" && GS.getActiveSubscriptionType() !== "Standard",
      isMaxLength: false,
      loadUuid: "",
      runQuery: false,
      error: false,
      queryRunning: false,
      isErrored: null,
      queryValid: false,
      yasqeQuery: null,
      isDirty: true,
      introspectedFail: false,
      queryType: "SELECT",
      isSelected: false,
      project: null,
      classList: [],
      propertyList: [],
      loadingProject: false,
      firstEdit: true,
      initialQuery: null,
      introspectMessage: "",
      yasqeKey: "",
      skip: false,
      runTime: 0,
      length: 0,
      viewType: 0,
      additionalProjects: []
    };
    this.cancellable = null;
    this.cancellable1 = null;
  }

  runQuery = () => {
    try {
      const subscriptionId = GS.getActiveSubscription().substring(39);
      const bucket = GS.getActiveSubscriptionBucket();
      const endpoint = GS.getEndpoint() + "/subscription/" + subscriptionId + "/sparql";
      const graph = getGraphFromProject(this.state.project);
      const additionalGraphs = this.state.additionalProjects.map(ap => getGraphFromProject(ap));
      const graphList = [graph, ...additionalGraphs];
      this.cancellable1 = makeCancelable(this.state.yasqeQuery({
        endpoint,
        headers: {
          "Authorization": "Bearer " + GS.getSessionToken(),
          "Graphologi-Target": bucket
        },
        namedGraphs: graphList
      }));
    }
    catch (e) {
      this.setState({
        isErrored: {
          message: "Could not run query."
        }
      });
    }
  };

  skipLoading = () => this.setState({ skip: true });

  isSkipping = (reject) => {
    if (this.state.skip) reject();
    if (this.skipTimeout) clearTimeout(this.skipTimeout);
    this.skipTimeout = setTimeout(() => this.isSkipping(reject), 1000);
  };

  getInfo = async (graph, loadUuid, resolve, reject) => {
    let classList = [];
    let propertyList = [];
    this.setState({ introspectMessage: "Loading classes..." });
    try {
      const classListResult = await GS.sparql(graph, `SELECT DISTINCT ?type {?s a ?type FILTER (!ISBLANK(?type))} LIMIT 1000`);
      if (this.cancellable && this.cancellable.isCancelled()) {
        reject();
        return;
      }
      if (this.state.skip || loadUuid !== this.state.loadUuid) {
        reject();
        return;
      }
      classList = classListResult.results.bindings.map(result => result.type.value).sort();
      this.setState({ introspectMessage: "Classes loaded. Loading properties..." });
      const propertyListResult = await GS.sparql(graph, `SELECT DISTINCT ?property {?s ?property ?o} LIMIT 1000`);
      if (this.cancellable && this.cancellable.isCancelled()) {
        reject();
        return;
      }
      if (this.state.skip || loadUuid !== this.state.loadUuid) {
        reject();
        return;
      }
      propertyList = propertyListResult.results.bindings.map(result => result.property.value).sort();
      resolve({
        classList,
        propertyList
      });
    }
    catch (e) {
      // Would normally happen due to no project SPARQL permissions
      reject();
    }
  };

  getAutocompleteData = async (project, loadUuid) => {
    let introspectedFail = false;
    let classList = [];
    let propertyList = [];
    try {
      const graph = getGraphFromProject(project);
      const infoData = await new Promise((resolve, reject) => {
        this.getInfo(graph, loadUuid, resolve, reject);
        this.isSkipping(reject);
      });
      if (this.skipTimeout) clearTimeout(this.skipTimeout);
      classList = infoData.classList;
      propertyList = infoData.propertyList;
    }
    catch (e) {
      if (this.skipTimeout) clearTimeout(this.skipTimeout);
      introspectedFail = true;
    }
    return {
      introspectedFail,
      classList,
      propertyList
    };
  };

  doGetData(project, loadUuid) {
    this.cancellable = makeCancelable(this.getAutocompleteData(project, loadUuid));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.cancellable1) this.cancellable1.cancel();
    clearTimeout(this.skipTimeout);
    clearTimeout(this.loadingTimeout);
    clearTimeout(this.waitTimeout);
  }

  onYasqeSetup = (yasqe) => {
    const setQuery = function (query) {
      yasqe.setValue(query);
    };
    const getQuery = function () {
      return yasqe.getValue();
    };
    this.setState({ yasqeQuery: yasqe.query, setQuery, getQuery, queryValid: true });

    yasqe.on("beforeChange", (instance, changeObj) => {
      const origin = changeObj.origin;
      const newText = changeObj.text.join();
      // If we're setting of reducing the query length just use existing length
      const length = instance.getQueryWithValues().length + newText.length;
      if ((this.state.isMaxLength || length > MAX_QUERY_LENGTH) && (origin !== "setValue" && origin !== "+delete" && origin !== "cut")) {
        return changeObj.cancel();
      }
    });
    yasqe.on("change", (instance) => {
      const validQueryType = ["ASK", "SELECT", "DESCRIBE"].includes(yasqe.getQueryType());
      const length = instance.getQueryWithValues().length;
      const isMaxLength = length >= MAX_QUERY_LENGTH;
      this.setState({
        error: false,
        queryValid: validQueryType && yasqe.queryValid,
        isDirty: true,
        firstEdit: false,
        wait: false,
        isMaxLength,
        length,
        isSelected: false,
        queryType: yasqe.getQueryType()
      });
      clearTimeout(this.waitTimeout);
    });
    yasqe.on("query", () => {
      yasqe.gsStart = Date.now();
      yasqe.options.readOnly = true;
      this.setState({ queryRunning: true, isDirty: false, runTime: 0 });
    });
    yasqe.on("queryResponse", async (instance, req) => {
      if (this.cancellable1 && this.cancellable1.isCancelled()) return;
      const runTime = yasqe.gsStart ? Date.now() - yasqe.gsStart : 0;
      yasqe.options.readOnly = false;
      // Add in cancellation check here
      if (req instanceof Error) {
        this.setState({ error: true, queryRunning: false, data: null });
      }
      else {
        const data = yasqe.getQueryType() === "DESCRIBE" ?
          await req.text
          :
          JSON.parse(await req.text);
        this.setState({
          data,
          queryRunning: false,
          wait: true,
          runTime,
          resultsQueryType: yasqe.getQueryType()
        });
        this.waitTimeout = setTimeout(() => {
          this.setState({ wait: false });
        }, 3000);
      }
    });
  };

  setProject = async (project, lang, projectType) => {
    const loadUuid = uuidv4();
    this.setState({
      project: null,
      loadingProject: true,
      loadingDelayComplete: false,
      loadUuid,
      additionalProjects: [],
      skip: false,
      introspectMessage: "",
      introspectedFail: false
    }, async () => {
      this.loadingTimeout = setTimeout(() => {
        this.setState({ loadingDelayComplete: true });
      }, 1000);
      let introspectedFail = false;
      let classList = [];
      let propertyList = [];
      try {
        const acData = await this.doGetData(project, loadUuid).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (!acData.introspectedFail) {
          classList = acData.classList;
          propertyList = acData.propertyList;
        }
        introspectedFail = acData.introspectedFail;
        this.setState({
          project,
          classList,
          runTime: 0,
          propertyList,
          isDirty: true,
          firstEdit: true,
          lang,
          yasqeKey: project,
          projectType,
          introspectedFail,
          loadingProject: false
        });
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ isErrored: e, loadingProject: false });
      }
    });
  };

  addProject = async (project) => {
    let additionalProjects = [...this.state.additionalProjects];
    additionalProjects.push(project);
    const yasqeKey = this.state.firstEdit ?
      this.state.yasqeKey + "-ap"
      :
      this.state.yasqeKey;
    this.setState({
      additionalProjects,
      yasqeKey,
      isSelected: false
    });
  };

  selectQuery = (query, additionalProjects = []) => {
    const setQuery = this.state.setQuery;
    setQuery(query);
    this.setState({
      isSelected: true,
      runTime: 0,
      additionalProjects
    });
  };

  updateQuery = (queryIRI) => {
    const graph = getGraphFromProject(this.state.project);
    const locked = GS.isGraphLocked(graph);
    if (locked) {
      this.setState({ isErrored: { message: "This project is currently locked." } });
      return;
    }
    const getQuery = this.state.getQuery;
    const query = getQuery();
    const escapedQuery = query.replace(/\t/g, '    ');
    GS.updateProperty(queryIRI, graph, "query", escapedQuery);
    const queryData = GS.data(queryIRI, graph);
    const ap = this.state.additionalProjects;
    const projectsToRemove = queryData.queryProject ? queryData.queryProject.filter(qp => !ap.includes(qp)) : [];
    const projectsToAdd = ap ? ap.filter(p => !queryData.queryProject.includes(p)) : [];
    projectsToRemove.forEach(pr => GS.deleteProperty(queryIRI, graph, "queryProject", pr));
    projectsToAdd.forEach(pr => GS.addProperty(queryIRI, graph, "queryProject", pr));
    this.setState({ isSelected: true });
  };

  saveQuery = async ({ title, description, isReport }) => {
    try {
      const graph = getGraphFromProject(this.state.project);
      const locked = GS.isGraphLocked(graph);
      if (locked) {
        this.setState({ isErrored: { message: "This project is currently locked." } });
        return;
      }
      const getQuery = this.state.getQuery;
      const query = getQuery();
      // We replace tabs with spaces
      const escapedQuery = query.replace(/\t/g, '    ');
      const queryIRI = "https://grafsync.graphifi.com/data/query/" + uuidv4();
      let newQuery = {
        id: queryIRI,
        type: "SavedQuery",
        title: { [this.state.lang]: title },
        ...(description && description !== "") && { description: { [this.state.lang]: description } },
        isReport,
        queryProject: this.state.additionalProjects,
        query: escapedQuery,
        queryType: this.state.queryType
      };
      await GS.insertResource(newQuery, queryIRI, graph);
      this.setState({ initialQuery: queryIRI, isSelected: true });
    }
    catch (e) {
      this.setState({ isErrored: { message: "Could not save query." } });
    }
  };

  clearError = () => this.setState({ isErrored: false });

  setViewType = viewType => this.setState({ viewType });

  deleteProject = (project) => {
    let additionalProjects = [...this.state.additionalProjects];
    additionalProjects.splice(additionalProjects.indexOf(project), 1);
    this.setState({
      additionalProjects,
      isSelected: false
    });
  };

  copyGraph = (projectIRI) => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(getGraphFromProject(projectIRI));
      return;
    }
  };

  getQueryHash = () => {
    const getQuery = this.state.getQuery;
    const text = getQuery();
    let hash = 0, i, chr;
    if (text.length === 0) return hash;
    for (i = 0; i < text.length; i++) {
      chr = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0;
    }
    return hash;
  };

  processTSV = () => {

    this.worker = new WebWorker(workerCode);

    this.worker.addEventListener('message',
      (tsv) => {
        try {
          GS.handle('sparql-results-download');
          const url = window.URL.createObjectURL(new Blob([tsv.data], { type: 'text/tab-separated-values' }));
          const link = document.createElement('a');
          link.href = url;
          const queryHash = this.getQueryHash();
          const reportName = "graphologi_query_results_" + queryHash + ".tsv";
          link.setAttribute('download', reportName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
        catch (e) {
          this.setState({
            isErrored: {
              message: "An error occurred downloading the TSV file."
            }
          });
        }
      },
      false);

    this.worker.onerror = () => {
      this.setState({
        isErrored: {
          message: "An error occurred generating the TSV file."
        }
      });
    };

    this.worker.postMessage({
      data: this.state.data,
      options: { includeHeaders: false }
    });

  };

  render() {

    const { classes } = this.props;
    const { data, queryValid, queryRunning, queryType, error, initialQuery, isSelected, runTime, wait, additionalProjects, yasqeKey,
      lang, resultsQueryType, classList, propertyList, loadingDelayComplete, allowSavedQueries, introspectMessage, length,
      isDirty, project, isMaxLength, isErrored, projectType, introspectedFail, loadingProject, viewType } = this.state;

    const graph = project ? getGraphFromProject(project) : null;
    const locked = GS.isGraphLocked(graph) || (additionalProjects && additionalProjects.some(ap => GS.isGraphLocked(getGraphFromProject(ap))));
    const disabled = wait || !project || !queryValid || queryRunning || locked;

    return (
      <UserConsumer>
        {({ user, entitlements }) => {
          // These are global entitlements
          const canViewSavedQueries = entitlements.includes(ENTITLEMENTS.MANAGE_SAVED_QUERY) || entitlements.includes(ENTITLEMENTS.VIEW_SAVED_QUERY);
          return <div style={{ marginRight: 30 }}>
            {isErrored && <Errored data={isErrored} clearError={this.clearError} />}
            <div style={{ float: 'right', marginBottom: 12, marginTop: -30 }}>
              <SelectProjectDialog
                open={true}
                disabled={loadingProject}
                onAddProject={this.setProject}
                label="Select Project"
                includeIcon={false} />
            </div>
            {canViewSavedQueries && project &&
              <div data-test="query-manager" className={classes.queryManagerContainer}>
                {!loadingProject && <QueryManager
                  key={project + initialQuery}
                  initialQuery={initialQuery}
                  isReadOnly={!entitlements.includes(ENTITLEMENTS.MANAGE_SAVED_QUERY)}
                  isSelected={isSelected}
                  queryType={queryType}
                  reportOption={true}
                  disabled={!project || !queryValid || queryRunning || locked}
                  defaultLang={lang}
                  updateQuery={this.updateQuery}
                  setQuery={this.selectQuery}
                  graph={graph} />}
              </div>
            }
            <div style={{ float: 'left', width: canViewSavedQueries && !loadingProject ? 'calc(100% - 310px)' : '100%' }}>
              {project &&
                <div style={{
                  backgroundColor: '#e5e5e5',
                  borderTopLeftRadius: 4,
                  padding: 4,
                  borderTopRightRadius: 4
                }}>
                  {additionalProjects.length < 4 && !locked &&
                    <React.Fragment>
                      <div style={{ float: 'right', marginTop: 8, marginRight: 8 }}>
                        <SelectProjectDialog
                          open={false}
                          disabled={loadingProject}
                          exclude={[project, ...additionalProjects]}
                          onAddProject={this.addProject}
                          label="Add Additional Project"
                          dataTest="add-additional-project"
                          includeIcon={false} />
                      </div>
                      <div style={{ float: 'right', marginTop: 10, marginRight: 20 }}>
                        <Help textRef="SPARQL_SHORTCUTS" position={false} />
                      </div>
                    </React.Fragment>
                  }
                  <SparqlProjectCard
                    key={project}
                    id={project}
                    graph={graph} />
                  {additionalProjects && additionalProjects.length > 0 &&
                    <Table data-test="projects-viewer" style={{
                      backgroundColor: '#eeeeee',
                      borderRadius: 4,
                      maxWidth: '100%',
                      border: 'solid 1px #e5e5e5'
                    }}>
                      <TableHead>
                        <TableRow>
                          <TableCell component="th" className={classes.tableHeading} style={{ paddingLeft: 8 }}>Project</TableCell>
                          <TableCell component="th" className={classes.tableHeading}>Graph</TableCell>
                          <TableCell component="th" className={classes.tableHeading}></TableCell>
                          <TableCell component="th" className={classes.tableHeading}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow data-test="base-project">
                          <TableCell data-test="base-project-card" component="th" className={classes.tableCell}>
                            <SparqlProjectCard
                              compact={true}
                              id={project}
                              graph={graph} />
                          </TableCell>
                          <TableCell
                            component="td"
                            data-test="base-project-graph"
                            className={classes.tableCell}
                            style={{ fontSize: '12px', paddingTop: 12 }}>
                            {graph}
                          </TableCell>
                          <TableCell
                            component="td"
                            className={classes.tableCell}
                            data-test="base-project-copy"
                            style={{ paddingTop: 4 }}>
                            <Tooltip title="Copy graph IRI to the clipboard">
                              <span>
                                <IconButton data-test="copy-graph" size="small" onClick={() => this.copyGraph(project)}>
                                  <CopyToCipboard fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            component="td"
                            className={classes.tableCell}
                            style={{ paddingTop: 4 }}></TableCell>
                        </TableRow>
                        {[...additionalProjects].sort().map((ap, apIndex) =>
                          <TableRow key={ap}>
                            <TableCell
                              component="td"
                              data-test={"project-card-" + apIndex}
                              className={classes.tableCell}>
                              <SparqlProjectCard
                                compact={true}
                                id={ap}
                                handleNotAvailable={true}
                                graph={getGraphFromProject(ap)} />
                            </TableCell>
                            <TableCell
                              component="td"
                              className={classes.tableCell}
                              data-test={"project-graph-" + apIndex}
                              style={{ fontSize: '12px', paddingTop: 12 }}>
                              {getGraphFromProject(ap)}
                            </TableCell>
                            <TableCell
                              component="td"
                              className={classes.tableCell}
                              data-test={"project-copy-" + apIndex}
                              style={{ paddingTop: 4 }}>
                              <Tooltip title="Copy graph IRI to the clipboard">
                                <span>
                                  <IconButton data-test="copy-graph" size="small" onClick={() => this.copyGraph(ap)}>
                                    <CopyToCipboard fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell
                              component="td"
                              className={classes.tableCell}
                              data-test={"project-delete-" + apIndex}
                              style={{ paddingTop: 4 }}>
                              <Tooltip title='Remove project'>
                                <span>
                                  <IconButton data-test="delete-graph" size="small" onClick={() => this.deleteProject(ap)} className={classes.deleteButton}>
                                    <DeleteIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  }
                  {additionalProjects && additionalProjects.length > 0 &&
                    <div style={{ marginTop: 4 }}>
                      <Notification error={false} text="When using additional projects you will need to include GRAPH clauses in the query." />
                    </div>
                  }
                </div>
              }
              <div className={classes.yasguiContainer}>
                {loadingProject && loadingDelayComplete &&
                  <div style={{ marginTop: 0, paddingTop: 100, height: 140, textAlign: 'center' }}>
                    <Typography variant="body2">Loading data for autocompletion. {introspectMessage}</Typography>
                    <div style={{ marginTop: 12, marginBottom: 12 }}>
                      <SecondaryButton onClick={this.skipLoading} className={classes.button}>Skip</SecondaryButton>
                    </div>
                    <Loading size="large" />
                  </div>
                }
                <div>
                  {introspectedFail &&
                    <div style={{ marginTop: 4 }}>
                      <Notification error={false} text="Could not gather class and property information for project. This will affect autocomplete." dataTest="introspection-fail" />
                    </div>
                  }
                  {project && !loadingProject &&
                    <YasqeManager
                      classList={classList}
                      propertyList={propertyList}
                      key={yasqeKey}
                      initialQuery={additionalProjects && additionalProjects.length > 0 ?
                        "SELECT * WHERE {\n    GRAPH ?g {\n        ?s ?p ?o .\n    }\n}\nLIMIT 10"
                        :
                        "SELECT * WHERE {\n    ?s ?p ?o .\n}\nLIMIT 10"}
                      onSetup={this.onYasqeSetup} />
                  }
                  {isMaxLength && <Typography paragraph data-test="max-length" variant="body2">Maximum is {MAX_QUERY_LENGTH} characters.</Typography>}
                  {project && !loadingProject &&
                    <div>
                      <div style={{ height: 20, float: 'right' }}>
                        {length ?
                          <Typography data-test="char-count" align="right" variant="caption">{length + " of " + MAX_QUERY_LENGTH + " characters"}</Typography>
                          :
                          null
                        }
                        {runTime && runTime !== 0 ?
                          <Typography variant="caption">Execution time: {runTime} milliseconds</Typography>
                          :
                          null
                        }
                      </div>
                      <div data-test="query-tasks" style={{ width: '100%', marginBottom: data ? 0 : 24 }}>
                        {locked &&
                          <div style={{ marginTop: 4, marginBottom: 12 }}>
                            <Notification dataTest="locked-notification" error={false} text="This project or an additional project is currently locked." />
                          </div>
                        }
                        {isDirty ?
                          <PrimaryButton
                            endIcon={<PlayArrowIcon />}
                            disabled={disabled}
                            onClick={this.runQuery}>{
                              queryRunning ?
                                "Running"
                                :
                                "Run Query"
                            }</PrimaryButton>
                          :
                          <Tooltip title="Query unchanged since last run">
                            <span>
                              <SecondaryButton
                                endIcon={<PlayCircleIcon />}
                                disabled={disabled}
                                dataTest="run-again"
                                onClick={this.runQuery}>{
                                  queryRunning ?
                                    "Running"
                                    :
                                    "Run Query"
                                }</SecondaryButton>
                            </span>
                          </Tooltip>
                        }
                        {allowSavedQueries && entitlements.includes(ENTITLEMENTS.MANAGE_SAVED_QUERY) && !locked &&
                          <span data-test="access-save" style={{ marginLeft: 12 }}>
                            <ProjectContainer
                              key={project}
                              component={SaveQueryDialog}
                              id={project}
                              graph={getGraphFromProject(project)}
                              disabled={!queryValid || queryRunning}
                              onSaveQuery={this.saveQuery}
                              defaultLang={lang}
                              reportOption={queryType === "SELECT"} />
                          </span>
                        }
                      </div>
                      {error ?
                        <div style={{ marginTop: 30, paddingBottom: 30 }}>
                          <Typography data-test="query-error" variant="body2">There is an error in your query or it may be taking too long to run.</Typography>
                        </div>
                        :
                        data &&
                        <div data-test="sparql-results" style={{ paddingTop: 20, paddingBottom: 30, }}>
                          <div style={{
                            display: 'flex',
                            width: 'calc(100% - 24px)',
                            borderRadius: 4,
                            padding: '12px 12px 6px 12px',
                            backgroundColor: '#e5e5e5'
                          }}>
                            <Typography className={classes.subMainHeading} paragraph style={{ flex: 1 }}>Results</Typography>
                            <span style={{ marginRight: 12 }}>
                              <Tooltip title="View as table of SPARQL results">
                                <span>
                                  <IconButton data-test="table-view" disabled={viewType === 0 || resultsQueryType !== "SELECT"} className={classes.smallIconButton} onClick={() => this.setViewType(0)}>
                                    <TableChartIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </span>
                            <span style={{ marginRight: 12 }}>
                              <Tooltip title="Preview as report">
                                <span>
                                  <IconButton data-test="report-view" disabled={viewType === 1 || resultsQueryType !== "SELECT"} className={classes.smallIconButton} onClick={() => this.setViewType(1)}>
                                    <AssessmentIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </span>
                            <Tooltip title="Download results">
                              <span>
                                <IconButton data-test="results-download"
                                  disabled={resultsQueryType !== "SELECT" || !data || !data.results || data.results.bindings.length === 0}
                                  className={classes.smallIconButton}
                                  onClick={() => this.processTSV()}>
                                  <GetAppIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </div>
                          {viewType === 0 ?
                            <SRV data={data} queryType={resultsQueryType} />
                            :
                            projectType === "TaxonomyProject" ?
                              <VocabularyProjectInfoContainer
                                passthroughComponent={ReportResultsViewer}
                                key={project}
                                projectType={projectType}
                                resultsData={data}
                                id={project}
                                user={user}
                                defaultLang={lang}
                                graph={getGraphFromProject(project)}
                                project={project} />
                              :
                              <OntologyProjectInfoContainer
                                passthroughComponent={ReportResultsViewer}
                                key={project}
                                resultsData={data}
                                projectType={projectType}
                                id={project}
                                user={user}
                                defaultLang={lang}
                                graph={getGraphFromProject(project)}
                                project={project} />
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>;
        }}
      </UserConsumer>
    );
  }
}

SparqlManager.propTypes = {
  classes: PropTypes.object
};

export default withStyles(styles)(SparqlManager);